/* Connect a source — one of the two doors (BUILD-JOURNEY stage 1).
 * Five connectors: OpenAPI (spec URL or FILES), MongoDB / Redis (connection
 * JSON), MCP / Agent (name + url + token). Ingestion is ASYNC / non-blocking: submit
 * ENQUEUES a background job and CLOSES immediately (only the fast enqueue is
 * awaited) — the live SCAN / tools-list / spec-fetch / DB-introspection runs
 * server-side off the request thread. onJobStarted hands the job id to the
 * parent (SourcesPage), which polls it to done/failed + refreshes the list, so
 * the work survives this dialog unmounting and the screen is never blocked.
 *
 * A-0017 INTERNAL CONNECTIONS: typing connection details means the author is
 * shown — or handed — the org's secret. For connectors the platform already
 * provisions, the author instead PICKS a named internal connection and the
 * engine resolves it server-side; the submitted body then carries no
 * credential field at all (omitted, never sent empty). COS is the only entry
 * today, and the picker keys off the catalog row's `source_type`, so a second
 * connector is a new row from the engine, not a rewrite here.
 *
 * An internal connect still PROBES — the engine resolves the org's COS from
 * Vault and HeadBuckets the named bucket, exactly as the explicit-credentials
 * path does. A missing bucket / unconfigured Vault / unprovisioned org is
 * therefore a FAILED CONNECT, not a form validation error: it arrives on the
 * enqueue (caught here) or on the polled job (SourcesPage toasts errors[]). */
import React, {
    useEffect,
    useRef,
    useState
} from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from 'shared/components/ui/dialog';
import {
    AlertTriangle,
    Check,
    CircleDashed,
    FileUp,
    Loader2,
    Plug,
    ShieldCheck
} from 'lucide-react';
import {
    appEngineService,
    describeError,
    extractValidatorErrors
} from '../../api/appEngine.service';
import {
    useAppStudio
} from '../../AppStudioContext';
import CodeMirrorJson from '../../shared/CodeMirrorJson';
import {
    notify
} from 'shared/api/notifier';
import type {
    InternalConnection
} from '../../api/types';

const MONGO_SKELETON = '{\n    "uri": "mongodb://host:27017",\n    "database": "your_db"\n}';
const REDIS_SKELETON = '{\n    "host": "your-redis-host",\n    "port": 6379,\n    "password": ""\n}';

/** Source types that offer the internal-connection picker. The OWNER ruled
 * redis out entirely and deferred mongo, so COS is the whole list — one entry
 * here plus a catalog row from the engine is all a second connector costs. */
const INTERNAL_PICKER_TYPES: readonly string[] = ['cos'];

/** The single non-secret input an internal COS connection still needs from
 * the author, as the engine declares it in `requires[]`. Note the asymmetry:
 * `requires` names the path in the STORED document (`config.bucket`) while
 * the connect arg is flat (`connection.bucket`) — the engine does the
 * nesting. */
const REQ_BUCKET = 'config.bucket';

/* The engine wraps connect failures as a 400 with {detail:{errors:[...]}}
 * (ingest_bridge.IngestError) — surface those verbatim instead of axios's
 * opaque "Request failed with status code 400". Falls back to the generic
 * describeError for transport/non-validator failures. */
function describeConnectError(err: unknown): string {
    const errors = extractValidatorErrors(err);
    if (errors && errors.length > 0) return errors.join('\n');
    return describeError(err);
}

interface SpecFile {
    name: string;
    text: string;
    status: 'pending' | 'running' | 'done' | 'failed';
    resultId?: string;
    error?: string;
}

function FileRow({
    f
}: {
    f: SpecFile;
}) {
    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '4px 2px',
            fontSize: 'var(--aps-text-xs)'
        }}>
            {f.status === 'pending' && <CircleDashed size={13} color="var(--aps-text-subtle)" />}
            {f.status === 'running' && <Loader2 size={13} className="aps-spin" color="var(--aps-primary)" />}
            {f.status === 'done' && <Check size={13} color="var(--aps-success)" />}
            {f.status === 'failed' && <AlertTriangle size={13} color="var(--aps-danger)" />}
            <span className="aps-mono" style={{
                flexShrink: 0,
                maxWidth: 180,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
            }}>
                {f.name}
            </span>
            <span title={f.status === 'failed' ? f.error : undefined}
                style={{
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: f.status === 'failed'
                        ? 'var(--aps-danger)' : 'var(--aps-text-muted)'
                }}>
                {f.status === 'done' && (f.resultId
                    ? <span className="aps-mono">→ {f.resultId}</span> : 'connected')}
                {f.status === 'failed' && (f.error || 'failed')}
                {f.status === 'pending' && 'queued'}
                {f.status === 'running' && 'ingesting…'}
            </span>
        </div>
    );
}

type Props = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** A background ingest job was ENQUEUED (connect is async / non-blocking).
     * The parent (SourcesPage) owns polling it to done/failed + refreshing the
     * list, so the work SURVIVES this dialog closing/unmounting. */
    onJobStarted: (jobId: string, name: string) => void;
};

export default function ConnectSourceDialog({
    open,
    onOpenChange,
    onJobStarted
}: Props) {
    const {
        tenancy
    } = useAppStudio();
    const [sourceType, setSourceType] = useState<'openapi' | 'mongo' | 'mcp' | 'redis' | 'agent' | 'cos'>('openapi');
    const [specUrl, setSpecUrl] = useState('');
    const [files, setFiles] = useState<SpecFile[]>([]);
    const [mongoJson, setMongoJson] = useState(MONGO_SKELETON);
    const [mcpName, setMcpName] = useState('');
    const [mcpUrl, setMcpUrl] = useState('');
    const [mcpToken, setMcpToken] = useState('');
    const [agentName, setAgentName] = useState('');
    const [agentUrl, setAgentUrl] = useState('');
    const [agentToken, setAgentToken] = useState('');
    const [redisName, setRedisName] = useState('');
    const [redisJson, setRedisJson] = useState(REDIS_SKELETON);
    const [redisMatch, setRedisMatch] = useState('');
    const [cosName, setCosName] = useState('');
    const [cosEndpoint, setCosEndpoint] = useState('');
    const [cosBucket, setCosBucket] = useState('');
    const [cosAccessKey, setCosAccessKey] = useState('');
    const [cosSecretKey, setCosSecretKey] = useState('');
    /* A-0017 internal connections: the catalog the engine serves, the entry
     * the author picked ('' = type the details yourself), and the load state.
     * All four are cleared in the reset block after a successful submit. */
    const [internalConns, setInternalConns] = useState<InternalConnection[]>([]);
    const [internalId, setInternalId] = useState('');
    const [internalLoading, setInternalLoading] = useState(false);
    const [internalError, setInternalError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);

    /* Load the org's internal connections whenever the dialog is open on a
     * type that offers them. Failure is NOT fatal: the manual form stays,
     * and the picker says why it could not list. */
    const picksInternal = INTERNAL_PICKER_TYPES.includes(sourceType);
    useEffect(() => {
        if (!open || !picksInternal) return undefined;
        let alive = true;
        setInternalLoading(true);
        setInternalError(null);
        appEngineService.listInternalConnections(tenancy)
            .then((items) => {
                if (!alive) return;
                setInternalConns(Array.isArray(items) ? items : []);
            })
            .catch((err) => {
                if (!alive) return;
                setInternalConns([]);
                // Always a non-empty reason: an empty string would silently
                // swallow the failure and read as "no internal connections".
                setInternalError(describeError(err) || 'Request failed');
            })
            .finally(() => {
                if (alive) setInternalLoading(false);
            });
        return () => {
            alive = false;
        };
    }, [open, picksInternal, tenancy]);

    /* Entries for the CHOSEN connector — unprovisioned ones included, so the
     * author sees what the platform offers instead of concluding it offers
     * nothing. */
    const internalOptions = internalConns.filter(
        (c) => c && c.source_type === sourceType);
    /* Only a PROVISIONED pick counts: an unprovisioned chip is disabled, and
     * a stale id (list refreshed under a selection) falls back to the manual
     * form rather than submitting a connection the engine cannot resolve. */
    const selectedInternal = internalOptions.find(
        (c) => c.id === internalId && c.provisioned) || null;
    /* The engine DECLARES what the author must still supply. Missing/empty
     * requires[] is read as "the bucket", the only cos config there is. */
    const internalRequires = Array.isArray(selectedInternal?.requires)
        ? selectedInternal.requires : [];
    const needsBucket = !selectedInternal
        || internalRequires.length === 0
        || internalRequires.includes(REQ_BUCKET);

    /* One sentence under the picker, matched to what actually happened —
     * a failed listing must not read as "there are none". */
    let pickerHelp: string;
    if (internalError) {
        pickerHelp = "Could not list the organization's internal connections"
            + ` (${internalError}) — enter the details instead.`;
    } else if (selectedInternal) {
        pickerHelp = 'The platform resolves this connection server-side. Its'
            + ' credentials are never sent to your browser and never stored on'
            + ' this source.';
    } else if (internalLoading) {
        pickerHelp = 'Checking what this organization is provisioned with…';
    } else if (internalOptions.length === 0) {
        pickerHelp = 'This organization has no internal connection for this'
            + ' source type — enter the details below.';
    } else {
        pickerHelp = 'Pick an internal connection to use the storage this'
            + ' organization is already provisioned with — no keys to find,'
            + ' none to paste.';
    }

    const pickFiles = async (list: FileList | null) => {
        if (!list || list.length === 0) return;
        const read = await Promise.all(Array.from(list).map(async (file) => ({
            name: file.name,
            text: await file.text(),
            status: 'pending' as const
        })));
        setFiles((prev) => [...prev, ...read]);
        setSpecUrl('');
        if (fileRef.current) fileRef.current.value = '';
    };

    const setFileAt = (i: number, patch: Partial<SpecFile>) => {
        setFiles((prev) => prev.map((f, j) => (j === i ? {
            ...f,
            ...patch
        } : f)));
    };

    /** Enqueue one background ingest job per spec file — each returns a job id
     * the parent polls to completion. No per-file await-to-completion; only the
     * (fast) enqueue is awaited, then the dialog closes. */
    const submitBatch = async () => {
        setBusy(true);
        let started = 0;
        for (let i = 0; i < files.length; i += 1) {
            setFileAt(i, {
                status: 'running',
                error: undefined
            });
            try {
                const res = await appEngineService.connectSource(tenancy, {
                    source_type: 'openapi',
                    spec_text: files[i].text
                });
                if (res?.job_id) {
                    onJobStarted(res.job_id, files[i].name);
                    setFileAt(i, {
                        status: 'done'
                    });
                    started += 1;
                }
            } catch (err) {
                setFileAt(i, {
                    status: 'failed',
                    error: describeConnectError(err)
                });
            }
        }
        setBusy(false);
        if (started > 0) {
            notify(`Ingesting ${started} spec${started > 1 ? 's' : ''} in the background…`,
                'success', 3);
            setFiles([]);
            onOpenChange(false);
        }
    };

    const submitSingle = async () => {
        let body: any = null;
        if (sourceType === 'openapi') {
            if (!specUrl.trim()) {
                notify('Provide a spec URL or pick spec files', 'warning');
                return;
            }
            body = {
                source_type: 'openapi',
                spec_url: specUrl.trim()
            };
        } else if (sourceType === 'mcp') {
            if (!mcpUrl.trim()) {
                notify('Provide the MCP server URL', 'warning');
                return;
            }
            if (!mcpName.trim()) {
                notify('Provide a name for the source', 'warning');
                return;
            }
            body = {
                source_type: 'mcp',
                name: mcpName.trim(),
                url: mcpUrl.trim()
            };
            if (mcpToken.trim()) {
                body.auth = {
                    method: 'bearer',
                    credentials: {
                        values: {
                            token: mcpToken.trim()
                        }
                    }
                };
            }
        } else if (sourceType === 'agent') {
            if (!agentUrl.trim()) {
                notify('Provide the agent URL', 'warning');
                return;
            }
            if (!agentName.trim()) {
                notify('Provide a name for the source', 'warning');
                return;
            }
            body = {
                source_type: 'agent',
                name: agentName.trim(),
                url: agentUrl.trim()
            };
            if (agentToken.trim()) {
                body.auth = {
                    method: 'bearer',
                    credentials: {
                        values: {
                            token: agentToken.trim()
                        }
                    }
                };
            }
        } else if (sourceType === 'mongo') {
            try {
                body = {
                    source_type: 'mongo',
                    connection: JSON.parse(mongoJson)
                };
            } catch {
                notify('Connection JSON is not valid JSON', 'warning');
                return;
            }
        } else if (sourceType === 'cos' && selectedInternal) {
            /* INTERNAL COS (A-0017): the engine resolves the org's COS from
             * Vault via `connection.internal`. SINGULAR + FLAT, matching
             * every other connector's connect args — BeXO refuses the plural
             * document shape by name. `bucket` sits directly on `connection`
             * (the engine is what nests it under `config` when it stores the
             * document), and there is NO `auth` key and no credential field
             * of any kind, so nothing secret ever transits the browser.
             *
             * `region` is not collected on this form — nor on the manual cos
             * form — so it is not sent. It is optional upstream. */
            if (!cosName.trim()) {
                notify('Provide a name for the source', 'warning');
                return;
            }
            if (needsBucket && !cosBucket.trim()) {
                notify('Provide the bucket', 'warning');
                return;
            }
            const connection: any = {
                internal: selectedInternal.id
            };
            if (needsBucket) connection.bucket = cosBucket.trim();
            body = {
                source_type: 'cos',
                name: cosName.trim(),
                connection
            };
        } else if (sourceType === 'cos') {
            if (!cosName.trim()) {
                notify('Provide a name for the source', 'warning');
                return;
            }
            if (!cosEndpoint.trim()) {
                notify('Provide the COS endpoint URL', 'warning');
                return;
            }
            if (!cosBucket.trim()) {
                notify('Provide the bucket', 'warning');
                return;
            }
            if (!cosAccessKey.trim()) {
                notify('Provide the access key', 'warning');
                return;
            }
            if (!cosSecretKey.trim()) {
                notify('Provide the secret key', 'warning');
                return;
            }
            body = {
                source_type: 'cos',
                name: cosName.trim(),
                connection: {
                    endpoint_url: cosEndpoint.trim(),
                    bucket: cosBucket.trim()
                },
                auth: {
                    method: 'hmac',
                    credentials: {
                        values: {
                            access_key: cosAccessKey.trim(),
                            secret_key: cosSecretKey.trim()
                        }
                    }
                }
            };
        } else {
            let connection: any;
            try {
                connection = JSON.parse(redisJson);
            } catch {
                notify('Connection JSON is not valid JSON', 'warning');
                return;
            }
            body = {
                source_type: 'redis',
                connection
            };
            if (redisName.trim()) body.name = redisName.trim();
            if (redisMatch.trim()) body.match = redisMatch.trim();
        }
        setBusy(true);
        const name = body.name || (sourceType === 'openapi' ? specUrl.trim()
            : sourceType === 'mongo' ? 'MongoDB source' : `${sourceType} source`);
        try {
            // ASYNC: only the (fast) enqueue is awaited — the live ingest runs
            // in the background. The parent polls the returned job to completion.
            const res = await appEngineService.connectSource(tenancy, body);
            setSpecUrl('');
            setMongoJson(MONGO_SKELETON);
            setMcpName('');
            setMcpUrl('');
            setMcpToken('');
            setAgentName('');
            setAgentUrl('');
            setAgentToken('');
            setRedisName('');
            setRedisJson(REDIS_SKELETON);
            setRedisMatch('');
            setCosName('');
            setCosEndpoint('');
            setCosBucket('');
            setCosAccessKey('');
            setCosSecretKey('');
            // A-0017 — every internal-connection state var resets too, or the
            // previous entry leaks into the next submission. The catalog is
            // re-fetched by the load effect the next time the dialog opens.
            setInternalId('');
            setInternalConns([]);
            setInternalLoading(false);
            setInternalError(null);
            if (res?.job_id) onJobStarted(res.job_id, name);
            notify(`Ingesting ${name} in the background…`, 'success', 3);
            onOpenChange(false);
        } catch (err) {
            notify(describeConnectError(err), 'danger');
        } finally {
            setBusy(false);
        }
    };

    const submit = () => {
        if (busy) return;
        if (sourceType === 'openapi' && files.length > 0) {
            submitBatch();
            return;
        }
        submitSingle();
    };

    const pendingCount = files.filter((f) => f.status !== 'done').length;

    return (
        <Dialog open={open} onOpenChange={(o) => {
            if (busy) return;
            onOpenChange(o);
        }}>
            <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                    <DialogTitle style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                    }}>
                        <Plug size={18} color="var(--aps-primary)" />
                        Connect a source
                    </DialogTitle>
                    <DialogDescription>
                        The Ingester reads the system and emits a draft source
                        document. Fix shapes, add capabilities, delete noise — it is
                        your JSON the moment it lands.
                    </DialogDescription>
                </DialogHeader>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12
                }}>
                    <div className="aps-chip-row">
                        {(['openapi', 'mongo', 'mcp', 'redis', 'agent', 'cos'] as const).map((t) => (
                            <button key={t}
                                className={'aps-filter-chip' + (sourceType === t ? ' active' : '')}
                                onClick={() => setSourceType(t)}>
                                {t === 'openapi' ? 'OpenAPI' : t === 'mongo' ? 'MongoDB'
                                    : t === 'mcp' ? 'MCP' : t === 'redis' ? 'Redis'
                                        : t === 'agent' ? 'Agent' : 'COS'}
                            </button>
                        ))}
                    </div>
                    {picksInternal && (
                        <div>
                            <label className="aps-label">Connection</label>
                            <div className="aps-chip-row">
                                <button
                                    className={'aps-filter-chip'
                                        + (selectedInternal ? '' : ' active')}
                                    title="Type the connection details yourself."
                                    onClick={() => setInternalId('')}>
                                    Enter details
                                </button>
                                {internalOptions.map((c) => (
                                    <button key={c.id}
                                        className={'aps-filter-chip'
                                            + (selectedInternal?.id === c.id ? ' active' : '')}
                                        disabled={!c.provisioned}
                                        title={c.provisioned
                                            ? (c.description || c.name)
                                            : (c.unprovisioned_reason
                                                || 'Not provisioned for this organization.')}
                                        onClick={() => setInternalId(c.id)}>
                                        {c.name}
                                    </button>
                                ))}
                                {internalLoading && (
                                    <Loader2 size={13} className="aps-spin"
                                        color="var(--aps-text-subtle)" />
                                )}
                            </div>
                            <p className="aps-help">{pickerHelp}</p>
                        </div>
                    )}
                    {sourceType === 'openapi' ? (
                        <>
                            <div>
                                <label className="aps-label">Spec URL</label>
                                <input className="aps-input aps-input-mono"
                                    placeholder="https://api.example.com/openapi.json"
                                    value={specUrl}
                                    disabled={files.length > 0}
                                    onChange={(e) => setSpecUrl(e.target.value)} />
                            </div>
                            <div>
                                <label className="aps-label">…or spec files</label>
                                <input ref={fileRef} type="file" multiple
                                    accept=".json,.yaml,.yml"
                                    style={{
                                        display: 'none'
                                    }}
                                    onChange={(e) => pickFiles(e.target.files)} />
                                <div style={{
                                    display: 'flex',
                                    gap: 8,
                                    alignItems: 'center'
                                }}>
                                    <button className="aps-btn aps-btn-secondary aps-btn-sm"
                                        disabled={busy}
                                        onClick={() => fileRef.current?.click()}>
                                        <FileUp size={14} />
                                        Choose files
                                    </button>
                                    <span className="aps-help" style={{
                                        marginTop: 0
                                    }}>
                                        JSON or YAML — one source per file
                                    </span>
                                    {files.length > 0 && !busy && (
                                        <button className="aps-btn aps-btn-ghost aps-btn-sm"
                                            onClick={() => setFiles([])}>
                                            Clear
                                        </button>
                                    )}
                                </div>
                                {files.length > 0 && (
                                    <div style={{
                                        marginTop: 8,
                                        maxHeight: 160,
                                        overflowY: 'auto',
                                        border: '1px solid var(--aps-border)',
                                        borderRadius: 'var(--aps-radius-sm)',
                                        padding: '4px 8px'
                                    }}>
                                        {files.map((f, i) => (
                                            <FileRow key={`${f.name}:${i}`} f={f} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : sourceType === 'mongo' ? (
                        <div>
                            <label className="aps-label">Connection JSON</label>
                            <div className="aps-editor-frame" style={{
                                height: 150,
                                flex: 'none'
                            }}>
                                <CodeMirrorJson value={mongoJson} onChange={setMongoJson} />
                            </div>
                            <p className="aps-help">
                                Two accepted shapes: <code>{'{ uri, database }'}</code> (a
                                standard <code>mongodb://</code> string — the db can live in
                                the URI path) or <code>{'{ host, port, database }'}</code>.
                                Credentials belong in vault refs, not raw URIs, beyond dev.
                            </p>
                        </div>
                    ) : sourceType === 'mcp' ? (
                        <>
                            <div>
                                <label className="aps-label">Name</label>
                                <input className="aps-input"
                                    placeholder="My MCP server"
                                    value={mcpName}
                                    onChange={(e) => setMcpName(e.target.value)} />
                            </div>
                            <div>
                                <label className="aps-label">MCP Server URL</label>
                                <input className="aps-input aps-input-mono"
                                    placeholder="https://your-mcp-server.example.com/mcp"
                                    value={mcpUrl}
                                    onChange={(e) => setMcpUrl(e.target.value)} />
                            </div>
                            <div>
                                <label className="aps-label">Bearer token</label>
                                <input className="aps-input aps-input-mono"
                                    type="password"
                                    placeholder="Optional — sent as Authorization: Bearer …"
                                    value={mcpToken}
                                    onChange={(e) => setMcpToken(e.target.value)} />
                                <p className="aps-help">
                                    Optional. If set, it is stored as the source's bearer
                                    credential and sent on every call to the MCP server.
                                </p>
                            </div>
                        </>
                    ) : sourceType === 'agent' ? (
                        <>
                            <div>
                                <label className="aps-label">Name</label>
                                <input className="aps-input"
                                    placeholder="My agent"
                                    value={agentName}
                                    onChange={(e) => setAgentName(e.target.value)} />
                            </div>
                            <div>
                                <label className="aps-label">Agent URL (A2A)</label>
                                <input className="aps-input aps-input-mono"
                                    placeholder="https://your-agent.example.com/a2a"
                                    value={agentUrl}
                                    onChange={(e) => setAgentUrl(e.target.value)} />
                                <p className="aps-help">
                                    The agent speaks A2A (JSON-RPC): message/send ·
                                    tasks/get · tasks/cancel. Its skills land as
                                    capabilities you wire into an agent surface.
                                </p>
                            </div>
                            <div>
                                <label className="aps-label">Bearer token</label>
                                <input className="aps-input aps-input-mono"
                                    type="password"
                                    placeholder="Optional — sent as Authorization: Bearer …"
                                    value={agentToken}
                                    onChange={(e) => setAgentToken(e.target.value)} />
                                <p className="aps-help">
                                    Optional. If set, it is stored as the source's bearer
                                    credential and sent on every call to the agent.
                                </p>
                            </div>
                        </>
                    ) : sourceType === 'cos' && selectedInternal ? (
                        /* INTERNAL COS: the credential fields are GONE from
                         * the form, not disabled — endpoint and both keys are
                         * the engine's to resolve. Only the bucket is the
                         * author's, and it is required. */
                        <>
                            <div>
                                <label className="aps-label">Name</label>
                                <input className="aps-input"
                                    placeholder="My object storage"
                                    value={cosName}
                                    onChange={(e) => setCosName(e.target.value)} />
                            </div>
                            {needsBucket && (
                                <div>
                                    <label className="aps-label">Bucket</label>
                                    <input className="aps-input"
                                        placeholder="my-bucket"
                                        value={cosBucket}
                                        onChange={(e) => setCosBucket(e.target.value)} />
                                </div>
                            )}
                            <p className="aps-help" style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 6,
                                marginTop: 0
                            }}>
                                <ShieldCheck size={13} color="var(--aps-success)"
                                    style={{
                                        flexShrink: 0,
                                        marginTop: 1
                                    }} />
                                <span>
                                    Using <strong>{selectedInternal.name}</strong>.
                                    The endpoint and keys stay server-side — this
                                    source stores only the bucket you name, and no
                                    credential is sent from this browser.
                                </span>
                            </p>
                        </>
                    ) : sourceType === 'cos' ? (
                        <>
                            <div>
                                <label className="aps-label">Name</label>
                                <input className="aps-input"
                                    placeholder="My object storage"
                                    value={cosName}
                                    onChange={(e) => setCosName(e.target.value)} />
                            </div>
                            <div>
                                <label className="aps-label">Endpoint URL</label>
                                <input className="aps-input aps-input-mono"
                                    placeholder="https://s3.<region>.cloud-object-storage.appdomain.cloud"
                                    value={cosEndpoint}
                                    onChange={(e) => setCosEndpoint(e.target.value)} />
                            </div>
                            <div>
                                <label className="aps-label">Bucket</label>
                                <input className="aps-input"
                                    placeholder="my-bucket"
                                    value={cosBucket}
                                    onChange={(e) => setCosBucket(e.target.value)} />
                            </div>
                            <div>
                                <label className="aps-label">Access key</label>
                                <input className="aps-input aps-input-mono"
                                    placeholder="COS / S3 access key id"
                                    value={cosAccessKey}
                                    onChange={(e) => setCosAccessKey(e.target.value)} />
                            </div>
                            <div>
                                <label className="aps-label">Secret key</label>
                                <input className="aps-input aps-input-mono"
                                    type="password"
                                    placeholder="COS / S3 secret access key"
                                    value={cosSecretKey}
                                    onChange={(e) => setCosSecretKey(e.target.value)} />
                                <p className="aps-help">
                                    Connecting drafts capabilities in BeXO; the bytes
                                    never touch the platform (workflows return presigned
                                    URLs). The secret transits like the mongo/redis
                                    password does today — credentials belong in vault
                                    refs, not raw keys, beyond dev.
                                </p>
                            </div>
                        </>
                    ) : (
                        <>
                            <div>
                                <label className="aps-label">Name</label>
                                <input className="aps-input"
                                    placeholder="My Redis"
                                    value={redisName}
                                    onChange={(e) => setRedisName(e.target.value)} />
                            </div>
                            <div>
                                <label className="aps-label">Connection JSON</label>
                                <div className="aps-editor-frame" style={{
                                    height: 130,
                                    flex: 'none'
                                }}>
                                    <CodeMirrorJson value={redisJson} onChange={setRedisJson} />
                                </div>
                                <p className="aps-help">
                                    Two shapes: <code>{'{ host, port, password }'}</code> or
                                    {' '}<code>{'{ url: "redis://…" }'}</code>. The keyspace is
                                    SCAN-sampled to draft capabilities — in Redis the id lives in
                                    the key as a <code>{'{slot}'}</code> template you refine here.
                                </p>
                            </div>
                            <div>
                                <label className="aps-label">Key match pattern{' '}
                                    <span style={{
                                        color: 'var(--aps-text-subtle)',
                                        fontWeight: 400
                                    }}>(optional)</span></label>
                                <input className="aps-input aps-input-mono"
                                    placeholder="e.g. app:* — scopes the SCAN sample"
                                    value={redisMatch}
                                    onChange={(e) => setRedisMatch(e.target.value)} />
                            </div>
                        </>
                    )}
                    <button className="aps-btn aps-btn-primary" disabled={busy} onClick={submit}>
                        {busy ? <Loader2 size={15} className="aps-spin" /> : <Plug size={15} />}
                        {busy ? 'Ingesting…'
                            : files.length > 0 && sourceType === 'openapi' && pendingCount > 0
                                ? `Connect ${pendingCount} spec${pendingCount > 1 ? 's' : ''}`
                                : 'Connect'}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
